import type { FunctionalComponent } from 'preact';

const Chevron: FunctionalComponent<{ direction: 'prev' | 'next' }> = ({ direction }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d={direction === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
  </svg>
);

interface Props {
  /** 지금 보고 있는 페이지 (1부터) */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** 한 번에 보여줄 번호 개수. 이보다 많으면 현재 쪽 주변만 보여준다 */
  window?: number;
}

/**
 * 페이지 번호 이동.
 *
 * 웹진 목록(`webzine-list.tsx`)에 있던 마크업을 그대로 옮겨 왔다. 미션 목록에도
 * 같은 것이 필요해져서 한곳으로 모았다 — 스타일(`.wz-list-pagination`)을 함께 쓴다.
 *
 * ★ 페이지가 한 장뿐이면 아무것도 그리지 않는다. 부르는 쪽에서 따로 가리지 않아도 된다.
 *
 * ★ 번호가 많아지면 **현재 쪽 주변만** 보여준다. 전부 그리면 좁은 화면에서 줄이
 *   넘쳐 흐른다.
 */
export const Pager: FunctionalComponent<Props> = ({ page, totalPages, onChange, window = 5 }) => {
  if (totalPages <= 1) return null;

  const current = Math.min(Math.max(1, page), totalPages);

  // 현재 쪽을 가운데 두되, 양 끝에서는 그쪽으로 붙인다.
  const half = Math.floor(window / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(totalPages, start + window - 1);
  start = Math.max(1, end - window + 1);

  const numbers = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <nav class="wz-list-pagination">
      <button
        type="button"
        class="wz-list-page-btn wz-list-page-btn--prev"
        aria-label="이전 페이지"
        disabled={current === 1}
        onClick={() => onChange(current - 1)}
      >
        <Chevron direction="prev" />
      </button>

      <div class="wz-list-page-numbers">
        {numbers.map((n) => (
          <button
            key={n}
            type="button"
            class={
              n === current
                ? 'wz-list-page-btn wz-list-page-num active'
                : 'wz-list-page-btn wz-list-page-num'
            }
            aria-current={n === current ? 'page' : undefined}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>

      <button
        type="button"
        class="wz-list-page-btn wz-list-page-btn--next"
        aria-label="다음 페이지"
        disabled={current === totalPages}
        onClick={() => onChange(current + 1)}
      >
        <Chevron direction="next" />
      </button>
    </nav>
  );
};
